'use client';

import { OrbitControls } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { MOUSE } from 'three';
import { DrawMapObjects } from '@/components/property-selector/draw-components/draw-map-objects';
import { Toolbar } from '@/components/ui/toolbar';
import { MIN_ZOOM, MAX_ZOOM } from '@/state-machine/property-selector/machine';
import { usePropertySelectorMachine } from '@/state-machine/property-selector/use-property-selector-machine';
import { MapColors, type PatchedProject, type PatchedPropertyUnit, type Point } from '@/types';

const pos = [10, 10, 10];
const toolsByState = {
  idle: 'select',
  drawing: 'draw',
  panning: 'pan',
  saving: 'select',
};

type DrawProps = {
  project: PatchedProject;
  properties: PatchedPropertyUnit[];
  onClickSave?: (shape: Point[]) => void;
};

const Draw: React.FC<DrawProps> = ({ project, properties, onClickSave }: DrawProps) => {
  const {
    mapConfig: { mapImage, hoverColor, availableColor, soldColor },
  } = project ?? {
    mapConfig: {
      hoverColor: MapColors.hover,
      availableColor: MapColors.available,
      soldColor: MapColors.sold,
    },
  };
  const controlsRef = useRef<any>(null);
  const {
    state,
    handlers: {
      onDraw,
      onPan,
      onReset,
      onSelect,
      onPointerMove,
      onPointerDown,
      onPointerUp,
      onPointerOver,
      onPointerOut,
      onZoomChange,
      onDeleteShape,
    },
  } = usePropertySelectorMachine({
    shapes: properties.map((p) => p.demarcationShape ?? []),
    saveShape: async ({ input: { shape } }) => {
      onClickSave?.(shape);
    },
  });

  const { zoomLevel, newShape, nextPoint, localShapes } = state.context;

  useEffect(() => {
    function onEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (state.matches('drawing')) onReset();
      }
    }

    window.addEventListener('keydown', onEscape);

    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [onReset, state]);

  const zoomIn = useCallback(() => {
    if (!controlsRef?.current) return;
    controlsRef.current.dollyOut(1.6);
    onZoomChange(controlsRef.current.object.zoom);
  }, [onZoomChange]);

  const zoomOut = useCallback(() => {
    if (!controlsRef?.current) return;
    controlsRef.current.dollyIn(1.6);
    onZoomChange(controlsRef.current.object.zoom);
  }, [onZoomChange]);

  const enableZoom = state.matches('drawing') || state.matches('panning');

  const canvasChildren = useMemo(() => {
    return (
      <>
        <ambientLight />
        {/* @ts-ignore */}
        <pointLight position={pos} />

        <DrawMapObjects
          basemap={mapImage}
          demarcations={properties}
          activeShapePoints={newShape}
          nextPoint={nextPoint}
          tempDemarcations={localShapes}
          activeShapeColor={0x0000ff}
          showLegend={false}
          colorsSettings="onlyShowDefaultColor"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerOver={onPointerOver}
          onPointerOut={onPointerOut}
          hoverColor={hoverColor}
          availableColor={availableColor}
          soldColor={soldColor}
        />
        <OrbitControls
          ref={controlsRef}
          enableRotate={false}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          zoomSpeed={0.5}
          enableZoom={enableZoom}
          enablePan={state.matches('panning')}
          onEnd={({ target }: any) => {
            onZoomChange((target as any).object.zoom);
          }}
          mouseButtons={{
            LEFT: MOUSE.PAN,
          }}
          zoomToCursor
        />
      </>
    );
  }, [
    availableColor,
    enableZoom,
    hoverColor,
    localShapes,
    mapImage,
    newShape,
    nextPoint,
    onPointerDown,
    onPointerMove,
    onPointerOut,
    onPointerOver,
    onPointerUp,
    onZoomChange,
    properties,
    soldColor,
    state,
  ]);

  return (
    <div className="w-full h-full relative">
      <Canvas id="draw-canvas" orthographic={true} className="w-full auto bg-gray-700">
        {canvasChildren}
      </Canvas>
      <Toolbar
        enableDelete={localShapes.length > 0}
        selected={toolsByState[state.value]}
        enableZoom={enableZoom}
        onClickZoomIn={zoomIn}
        onClickZoomOut={zoomOut}
        zoomLevel={Math.floor(((zoomLevel - MIN_ZOOM) / (MAX_ZOOM - MIN_ZOOM)) * 100)}
        onClickDraw={() => onDraw()}
        onClickPan={() => onPan()}
        onClickSelect={() => onSelect()}
        onClickDelete={() => onDeleteShape()}
      />
    </div>
  );
};

export default Draw;
